import { useMemo } from 'react';
import { css } from '@emotion/react';
import { ScheduleDefinition } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule_definitions.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Chip, DropdownField, Heading, ModalControlModalProps, NakedButton, Tray } from '@frontend/design-system';
import { useAvailabilityRuleForm, useAvailabilityRuleFormData } from '../../hooks/settings';
import { useRuleRecurrenceForm } from '../../hooks/settings/use-rule-recurrence-form';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { AvailabilityRuleForm, AvailabilityMode } from './AvailabilityRuleForm';
import { GetAvailabilityRuleFormFieldProps } from './AvailabilityRuleForm/types';

type Props = {
  locationId?: string;
  selectedScheduleDefinition: ScheduleDefinition | null;
  modalProps: ModalControlModalProps;
  availabilityMode: AvailabilityMode;
  onClose: () => void;
  isEditMode: boolean | undefined;
  selectedResourceId: string | undefined;
};

export const AddEditAvailabilityRuleTray = ({
  locationId,
  selectedScheduleDefinition,
  modalProps,
  availabilityMode,
  selectedResourceId,
  onClose,
  isEditMode = false,
}: Props) => {
  const { t } = useTranslation('scheduleSettings');
  const { isMultiLocation, getScopeName } = useSchedulingLocationInfo();
  const showLocationChip = isMultiLocation && locationId;

  const { providerList, operatoryList } = useAvailabilityRuleFormData({
    locationId,
  });

  const { values, reset, getFieldProps, isComplete, handleSubmit, handleRRuleChange, rRule } = useAvailabilityRuleForm({
    locationId,
    selectedScheduleDefinition,
    availabilityMode,
    selectedResourceId,
    modalProps,
    isEditMode,
    providerList,
    operatoryList,
  });

  const ruleRecurrenceFormProps = useRuleRecurrenceForm({
    locationId,
    getAvailabilityRuleFieldProps: getFieldProps,
    values,
    rRule,
    handleRRuleChange,
  });

  const closeModal = () => {
    reset();
    onClose();
  };

  return (
    <Tray {...modalProps} width='large' onClose={closeModal}>
      <Tray.Header
        Buttons={
          <NakedButton onClick={closeModal}>
            <Icon name='x' size={24} />
          </NakedButton>
        }
      >
        <div css={{ display: 'flex', flexDirection: 'column' }}>
          <TrayHeading
            getFieldProps={getFieldProps}
            isEditMode={isEditMode}
            providerList={providerList}
            operatoryList={operatoryList}
            availabilityMode={availabilityMode}
          />
          {showLocationChip && <Chip.Location maxWidth={'fit-content'}>{getScopeName(locationId)}</Chip.Location>}
        </div>
      </Tray.Header>
      <Tray.Body css={{ borderTop: `1px solid ${theme.colors.neutral20}`, padding: theme.spacing(2, 1) }}>
        <AvailabilityRuleForm
          locationId={locationId}
          providerList={providerList}
          operatoryList={operatoryList}
          getFieldProps={getFieldProps}
          ruleRecurrenceFormProps={ruleRecurrenceFormProps}
          availabilityMode={availabilityMode}
        />
      </Tray.Body>
      <Tray.Actions
        disabledPrimary={!isComplete}
        primaryLabel={t('Save')}
        onPrimaryClick={handleSubmit}
        secondaryLabel={t('Cancel')}
        onSecondaryClick={closeModal}
        // TODO: Add primaryTrackingId, secondaryTrackingId
      />
    </Tray>
  );
};

const TrayHeading = ({
  getFieldProps,
  isEditMode,
  providerList,
  operatoryList,
  availabilityMode,
}: {
  getFieldProps: GetAvailabilityRuleFormFieldProps;
  isEditMode: boolean;
  providerList: { value: string; label: string }[];
  operatoryList: { value: string; label: string }[];
  availabilityMode: AvailabilityMode;
}) => {
  const { t } = useTranslation('scheduleSettings');
  const fieldProps =
    availabilityMode === 'PROVIDER' ? getFieldProps('serviceableProvider') : getFieldProps('operatory');
  const dropdownList = availabilityMode === 'PROVIDER' ? providerList : operatoryList;

  const title = useMemo(() => {
    const actionText = isEditMode ? t('Edit') : t('Add');
    const availabilityText = t('Availability for');

    return `${actionText} ${availabilityText}`;
  }, [isEditMode, t]);

  return (
    <div css={styles.trayHeading}>
      <Heading level={2}>{title}</Heading>
      <DropdownField label={''} {...fieldProps} css={styles.trayHeadingDropdown}>
        <DropdownField.Option key={'empty'} value={''}>
          {availabilityMode === 'PROVIDER' ? t('Select Provider') : t('Select Operatory')}
        </DropdownField.Option>
        {dropdownList?.map((item) => (
          <DropdownField.Option key={item.value} value={item.value}>
            {item.label}
          </DropdownField.Option>
        ))}
      </DropdownField>{' '}
    </div>
  );
};

const styles = {
  trayHeading: css`
    display: flex;
    gap: ${theme.spacing(1)};
    flex-wrap: wrap;
  `,
  trayHeadingDropdown: css`
    min-width: ${theme.spacing(20)};
  `,
};
